import { defineStore } from 'pinia'
import {
  convertArrayToObject,
  hideShowFullSpinner,
  cartHasFulfillment,
} from 'src/utils'
import { Platform } from 'quasar'
import { NotifyMethods } from 'src/boot/notify'
import { usePaymentStore } from 'src/stores/payment'
import { useAuthStore } from 'src/stores/auth'
import { useGuestStore } from 'src/stores/guest'
import { useCustomerStore } from 'src/stores/customer'
import { useOrderStore } from 'src/stores/order'
import { useReserveSlotStore } from 'src/stores/reserve-slot'
import { usePersistedLocalStore } from 'src/stores/persisted-local'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'
import { useGiftCardStore } from 'src/stores/gift-card'

function calculateCartTotal(cartDetail) {
  if (cartDetail) {
    let subTotalDisplay = 0,
      totalItems = 0

    for (const fId in cartDetail.fulfillments) {
      const fulfillment = cartDetail.fulfillments[fId]
      let totalQTY = 0

      for (let olId in fulfillment.shoppingCartItems) {
        const item = fulfillment.shoppingCartItems[olId]

        totalQTY += item.qty
        totalItems += item.qty
        subTotalDisplay += item.totalPrice

        if (item?.linkedProducts?.length) {
          item.linkedProducts.forEach((linkedProduct) => {
            totalQTY += linkedProduct.qty
            totalItems += linkedProduct.qty
            subTotalDisplay += linkedProduct.totalPrice
          })
        }
      }
      fulfillment.totalQTY = totalQTY
    }

    cartDetail.subTotalDisplay = subTotalDisplay
    cartDetail.totalItems = totalItems
  }

  return cartDetail
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    cartDetail: {},
    cartNumber: null,
    loading: false,
    currentOrderParameters: {
      orderNumber: null,
      paymentMethodId: null,
      orderFulfillments: [],
      tipAmount: null,
      isAcknowledgeAccepted: false,
      isGiftCardAllowForRecurringOrder: false,
      isAcknowledgedForDelivery: false,
    },
    checkoutInReviewMode: false,
    substituteDetail: {},
    recentSubstituteDetail: {},
    alertDetail: {},
    outOfStockProductsInfo: {},
    shippingRates: {},
    alertInfo: {},
    allowPlaceOrder: true,
    isTipConfirmed: false,
    checkoutSelection: null,
    isConfirmSubscription: false,
    payAtStore: false,
    deliveryPartners: {},
    rateLoader: false,
    isCheckoutDisabled: false,
  }),
  getters: {
    currentCartDetails() {
      return this.cartDetail[this.cartNumber]
    },
    getSubCartTotal() {
      if (this.currentCartDetails?.subTotalDisplay)
        return this.currentCartDetails.subTotalDisplay

      return '0.0'
    },
    cartTotalItems() {
      if (this.currentCartDetails) return this.currentCartDetails.totalItems
      return 0
    },
    currentCartNumber: (state) => state.cartNumber,
    substituteDetails: (state) => state.substituteDetail,
    pickupInfoByFulfillmentId(state) {
      return (fulfillmentId) => {
        for (let fulfillment of state.currentOrderParameters
          .orderFulfillments) {
          if (fulfillmentId == fulfillment.fulfillmentMethodID) {
            return fulfillment.pickupPerson
          }
        }
        return null
      }
    },
    selectedPaymentMethod(state) {
      const paymentStore = usePaymentStore()
      if (state.currentOrderParameters.paymentMethodID) {
        return paymentStore.paymentMethods[
          state.currentOrderParameters.paymentMethodID
        ]
        // rootState.payment.availableMethods[
        //   state.currentOrderParameters.paymentMethodID
        // ]
      }
      return null
    },
    selectedPaymentMethodId: (state) =>
      state.currentOrderParameters.paymentMethodID,
    substitute(state) {
      return (orderLineItemId, allowSubstitute) => {
        if (this.recentSubstituteDetail.hasOwnProperty(orderLineItemId))
          return this.recentSubstituteDetail[orderLineItemId].allowSubstitute

        if (this.substituteDetails.hasOwnProperty(orderLineItemId))
          return this.substituteDetails[orderLineItemId].allowSubstitute

        return allowSubstitute
      }
    },
    isSubstitutionAllowed(state) {
      let isTrueFound = false,
        isFalseFound = false

      if (this.currentCartDetails && this.currentCartDetails.fulfillments) {
        for (let fId in this.currentCartDetails.fulfillments) {
          const fulfillment = this.currentCartDetails.fulfillments[fId]
          if (fulfillment.codeName !== 'sd') {
            for (let olId in fulfillment.shoppingCartItems) {
              const item = fulfillment.shoppingCartItems[olId]
              if ([2, 3].includes(item.orderLineItemOrderType)) {
                continue
              }
              if (this.substitute(item.orderLineItemID, item.allowSubstitute)) {
                isTrueFound = true
              } else {
                isFalseFound = true
              }

              if (isTrueFound && isFalseFound) {
                return null
              }
            }
          }
        }
      }

      return isTrueFound
    },
    showSubstitution(state) {
      const fulfillments = this.currentCartDetails?.fulfillments
      if (!fulfillments) return false

      const fulfillmentArray = Object.values(fulfillments)
      if (
        !fulfillmentArray.length ||
        (fulfillmentArray.length === 1 && fulfillmentArray[0].codeName === 'sd')
      ) {
        return false
      }

      return fulfillmentArray.some((fulfillment) =>
        Object.values(fulfillment.shoppingCartItems).some(
          (item) => ![2, 3].includes(item.orderLineItemOrderType)
        )
      )
    },
    getAlertErrors: (state) => state.alertDetail,
    cartHasInStore() {
      let currentCartDetails = this.currentCartDetails,
        hasFulfillments = false

      if (currentCartDetails?.fulfillments) {
        for (const fId in currentCartDetails.fulfillments) {
          const fulfillment = currentCartDetails.fulfillments[fId]
          if (fulfillment.codeName == 'is') {
            hasFulfillments = true
            break
          }
        }
      }
      return hasFulfillments
    },
    cartHasCurbside() {
      let currentCartDetails = this.currentCartDetails,
        hasFulfillments = false

      if (currentCartDetails?.fulfillments) {
        for (const fId in currentCartDetails.fulfillments) {
          const fulfillment = currentCartDetails.fulfillments[fId]
          if (fulfillment.codeName == 'cs') {
            hasFulfillments = true
            break
          }
        }
      }
      return hasFulfillments
    },
    outOfStockProducts() {
      return this.outOfStockProductsInfo[this.cartNumber] || []
    },
    cartHasAllOutOfStockProducts() {
      let currentCartDetails = this.currentCartDetails
      let outOfStockProductsItemIDs = this.outOfStockProducts.map(
        (product) => product.orderLineItemID
      )
      let cartItemIDs = []
      for (const fId in currentCartDetails.fulfillments) {
        const fulfillment = currentCartDetails.fulfillments[fId]
        for (const shoppingCartItem in fulfillment.shoppingCartItems) {
          const olId = fulfillment.shoppingCartItems[olId]
          cartItemIDs.push(shoppingCartItem.orderLineItemID)
        }
      }
      return (
        cartItemIDs.sort().toString() ==
        outOfStockProductsItemIDs.sort().toString()
      )
    },
    substituteChildProductDetails() {
      return (orderLineItemId, allowSubstitute) => {
        if (this.recentSubstituteDetail.hasOwnProperty(orderLineItemId))
          return this.recentSubstituteDetail[orderLineItemId].substituteProducts
        return []
      }
    },
    selectedChildSubstituteProduct() {
      return (orderLineItemId, allowSubstitute) => {
        if (this.recentSubstituteDetail.hasOwnProperty(orderLineItemId))
          return this.recentSubstituteDetail[orderLineItemId]
            .selectedSubstituteProduct
        return ''
      }
    },
    currentCartFulfillment() {
      const fulfillments = this.currentCartDetails?.fulfillments || {}
      return Object.values(fulfillments)[0] || {}
    },
    selectedShippingRate() {
      const { rateID } = this.checkoutSelection || {}
      const { shippingRateID } = this.currentCartFulfillment || {}
      const shippingRates = this.shippingRates
      const isShippingRateApply = !!this.currentCartDetails?.isShippingRateApply

      return isShippingRateApply
        ? shippingRates[rateID] ||
            shippingRates[shippingRateID] ||
            shippingRates[Object.keys(shippingRates)[0]] ||
            ''
        : ''
    },
    selectedShippingRateId() {
      const isShippingRateApply = !!this.currentCartDetails?.isShippingRateApply
      return isShippingRateApply
        ? this.selectedShippingRate?.rateID ||
            this.selectedShippingRate?.shippingRateID ||
            ''
        : ''
    },
    getProductById() {
      return (productId) => {
        const persistedStore = usePersistedStore()
        let productInfo = null
        if (this.currentCartDetails?.fulfillments) {
          for (let fId in this.currentCartDetails.fulfillments) {
            const fulfillment = this.currentCartDetails.fulfillments[fId]
            if (
              fulfillment.codeName == persistedStore.selectedFulfillmentCodeName
            ) {
              for (let olId in fulfillment.shoppingCartItems) {
                const item = fulfillment.shoppingCartItems[olId]
                if (item.productId == productId) {
                  productInfo = item
                  break
                }
              }
            }
            if (productInfo) break
          }
        }

        return productInfo
      }
    },
    alertData: (state) => (alertKey) => state.alertInfo[alertKey] || null,
    isAcknowledgeAccepted: (state) =>
      state.currentOrderParameters.isAcknowledgeAccepted,
    getSubscribedProducts() {
      const persistedLocalStore = usePersistedLocalStore()
      let orderSubscriptions = [],
        currentCartDetails = this.currentCartDetails,
        cartPersistedInfo = persistedLocalStore.getCartPersistedInfo(
          currentCartDetails.orderNumber
        )
      if (
        currentCartDetails?.fulfillments &&
        cartPersistedInfo?.subscriptionDetails
      ) {
        const persistedStore = usePersistedStore()
        for (const fId in currentCartDetails.fulfillments) {
          const fulfillment = currentCartDetails.fulfillments[fId]
          if (
            fulfillment.codeName == persistedStore.selectedFulfillmentCodeName
          ) {
            for (let olId in fulfillment.shoppingCartItems) {
              const item = fulfillment.shoppingCartItems[olId],
                subscriptionDetail =
                  cartPersistedInfo.subscriptionDetails[item.orderLineItemID]
              if (
                item.isSubscribeForAutoShip &&
                subscriptionDetail.subscriptionOption == 2 &&
                subscriptionDetail.productID &&
                subscriptionDetail.frequency
              )
                orderSubscriptions.push({
                  productID: subscriptionDetail.productID,
                  frequency: subscriptionDetail.frequency,
                })
            }
          }
        }
      }
      return orderSubscriptions
    },
    cartHasOnlyGiftCard() {
      const fulfillments = this.currentCartDetails?.fulfillments
      if (!fulfillments) return false

      return Object.values(fulfillments).some((fulfillment) =>
        Object.values(fulfillment.shoppingCartItems).every((item) =>
          [2, 3].includes(item.orderLineItemOrderType)
        )
      )
    },
    cartHasOnlyDigitalGiftCard() {
      const fulfillments = this.currentCartDetails?.fulfillments
      if (!fulfillments) return false

      return Object.values(fulfillments).some((fulfillment) =>
        Object.values(fulfillment.shoppingCartItems || {}).every(
          (item) => item?.orderLineItemOrderType === 2
        )
      )
    },
    cartHasDigitalGiftCard() {
      const fulfillments = this.currentCartDetails?.fulfillments
      if (!fulfillments) return false

      return Object.values(fulfillments).some((fulfillment) =>
        Object.values(fulfillment.shoppingCartItems || {}).some(
          (item) => item?.orderLineItemOrderType === 2
        )
      )
    },
    isWarehouseLocationSelected() {
      const persistedStore = usePersistedStore()
      return !!(
        this.currentCartDetails?.shipToStoreLocationID ==
        persistedStore.currentLocationId
      )
    },
    shippingCharge() {
      const persistedStore = usePersistedStore()
      const commonStore = useCommonStore()

      let rateObj = {}

      if (
        persistedStore.selectedFulfillmentCodeName === 'sd' &&
        !this.isWarehouseLocationSelected &&
        commonStore.currentRoute.name === 'Checkout'
      ) {
        rateObj = this.selectedShippingRate
      }

      return rateObj?.finalAmount || 0
    },
    additionalCharges() {
      const persistedStore = usePersistedStore()
      const commonStore = useCommonStore()
      let responseObj = {}

      if (
        persistedStore.selectedFulfillmentCodeName === 'sd' &&
        commonStore.currentRoute.name === 'Checkout'
      ) {
        let fulfillment = Object.values(
          this.currentCartDetails?.fulfillments || {}
        ).find((x) => x.codeName == 'sd')

        let rate = this.selectedShippingRate

        let minOrderAdditionalCharge =
          this.currentCartDetails?.orderSummary?.additionalCharges.find(
            (x) => x.addChargeType == 1
          )
        if (
          this.currentCartDetails?.orderSummary?.shippingRate !=
            rate?.finalAmount &&
          this.currentCartDetails?.orderSummary?.finalPayAmount +
            rate?.finalAmount -
            minOrderAdditionalCharge?.chargeAmount >
            fulfillment.minCheckoutAmount
        ) {
          responseObj.charges =
            this.currentCartDetails.orderSummary.additionalCharges.filter(
              (x) => x.addChargeType != 1
            )
          responseObj.minOrderFixedCharge = fulfillment.fixedCharge
          return responseObj
        }
      }

      responseObj.charges =
        this.currentCartDetails.orderSummary.additionalCharges
      responseObj.minOrderFixedCharge = 0
      return responseObj
    },
    orderTotal() {
      return (
        Number(
          (
            this.currentCartDetails.orderSummary.finalPayAmount +
            this.deliveryCharge +
            this.shippingCharge -
            this.additionalCharges.minOrderFixedCharge
          ).toFixed(2)
        ) || 0
      )
    },
    deliveryCharge() {
      const commonStore = useCommonStore()
      let amount = 0,
        fulfillment = Object.values(
          this.currentCartDetails?.fulfillments || {}
        ).find((x) => x.codeName == 'scd')
      if (!fulfillment) return amount
      if (
        commonStore.currentRoute?.name === 'Checkout' &&
        fulfillment?.deliveryMethod === 1
      ) {
        const deliveryPartnerID =
          this.checkoutSelection?.deliveryPartnerID ||
          fulfillment.deliveryPartnerID
        let rateObj = this.deliveryPartners[deliveryPartnerID]
        amount += rateObj?.shippingPrice || 0
      }
      return amount
    },
    isGiftCardAllowForRecurringOrder() {
      return (
        this.currentOrderParameters.isGiftCardAllowForRecurringOrder || false
      )
    },
    isAcknowledgedForDelivery() {
      return this.currentOrderParameters.isAcknowledgedForDelivery || false
    },
    isUberSandboxCodeVerified() {
      const persistedLocalStore = usePersistedLocalStore()
      return !!(
        this.currentCartNumber ==
        persistedLocalStore.uberSandBoxVerifiedCartNumber
      )
    },
    isDoordashSandboxCodeVerified() {
      const persistedLocalStore = usePersistedLocalStore()
      return !!(
        this.currentCartNumber ==
        persistedLocalStore.doordashSandBoxVerifiedCartNumber
      )
    },
    isFloorPriceAppliedOnCart() {
      const persistedStore = usePersistedStore()
      const fulfillmentId = persistedStore.selectedFulfillment?.fulfillmentId

      const products = Object.values(
        this.currentCartDetails?.fulfillments?.[fulfillmentId]
          ?.shoppingCartItems || {}
      )

      for (const product of products) {
        // Check main product
        if (product.isFloorPriceApplicable) {
          return true
        }

        // Check linked products immediately
        if (product.linkedProducts?.length) {
          for (const linkedProduct of product.linkedProducts) {
            if (linkedProduct.isFloorPriceApplicable) {
              return true
            }
          }
        }
      }

      return false
    },
  },
  actions: {
    async getAlreadySubscribeProducts(params = {}) {
      try {
        let response = await this.$axiosInstance.get(
          'v1/Cart/GetAlreadySubscribeProducts',
          { params }
        )
        return response.data
      } catch (error) {
        console.log('err', error)
        return error
      }
    },
    async getCartDetail({
      showFullLoader = false,
      isCheckoutPage = false,
      additionalParams = {},
    } = {}) {
      try {
        const authStore = useAuthStore(),
          guestStore = useGuestStore(),
          customerStore = useCustomerStore()

        this.loading = true
        if (showFullLoader) hideShowFullSpinner(true)

        let isLoggedIn = authStore.isLoggedIn,
          guestCartNumber = guestStore.guestCartNumber,
          verifiedGuestCustomerId = guestStore.verifiedGuestCustomerId,
          customerInfo = customerStore.customer,
          requestObj = {}

        if (guestCartNumber) {
          if (isLoggedIn) {
            if (
              !verifiedGuestCustomerId ||
              verifiedGuestCustomerId == customerInfo.customerID
            )
              requestObj.orderNumber = guestCartNumber
            guestStore.resetGuestUserInfo()
          } else requestObj.orderNumber = guestCartNumber
        }

        if (this.selectedShippingRateId && isCheckoutPage)
          requestObj['shippingRateID'] = this.selectedShippingRateId

        let { data } = await this.$axiosInstance.get('v1/Cart/GetOrderNumber', {
          params: { ...requestObj, ...additionalParams },
        })

        if (data?.data?.orderNumber) {
          this.cartNumber = data.data.orderNumber
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data ? data.data : {},
          })
          if (this.isCheckoutDisabled) this.isCheckoutDisabled = false
        } else if (data.data == null) {
          /* remove guest cart number for guest checkout */
          if (!isLoggedIn && guestCartNumber) {
            guestStore.resetGuestUserInfo()
          }

          this.cartNumber = null
          /* remove guest cart number for guest checkout */
        }

        // When guest user comes from buy now checkout page
        if (additionalParams.buyNowOrderNumber) {
          const persistedLocalStore = usePersistedLocalStore()
          if (
            persistedLocalStore.persistedCartNumber ==
            additionalParams.orderNumber
          ) {
            persistedLocalStore.setField({
              field: 'persistedCartNumber',
              value: null,
            })
          }
          if (
            !authStore.isLoggedIn &&
            additionalParams.orderNumber &&
            guestStore.guestCartNumber != additionalParams.orderNumber
          ) {
            guestStore.setGuestCartNumber(additionalParams.orderNumber)
          }
          guestStore.resetBuyNowOrderNumbers()
        }
        return data
      } catch (error) {
        return error
      } finally {
        hideShowFullSpinner(false)
        this.loading = false
      }
    },
    async addUpdateCartDetail(params) {
      try {
        const authStore = useAuthStore(),
          guestStore = useGuestStore(),
          persistedStore = usePersistedStore(),
          commonStore = useCommonStore()

        if (
          this.$router.currentRoute.name == 'Cart' &&
          params.apiVersion !== 'v1'
        )
          this.loading = true

        let locationID = persistedStore.currentLocationId,
          isLoggedIn = authStore.isLoggedIn,
          guestCartNumber = guestStore.guestCartNumber,
          requestObj = {
            ...params,
            locationID: locationID,
            zipCode: persistedStore.locationOrShippingZip,
          }

        /* Guest checkout: Pass orderNumber and slotBookingID parameter */
        if (!isLoggedIn) {
          if (guestCartNumber) requestObj.orderNumber = guestCartNumber
          let selectedFulfillmentCodeName =
              persistedStore.selectedFulfillmentCodeName,
            guestSlotBookingId = guestStore.slotBookingId(
              selectedFulfillmentCodeName
            ),
            isSlotBookingRequired = commonStore.isSlotBookingRequired
          if (guestSlotBookingId && isSlotBookingRequired)
            requestObj.slotBookingID = guestSlotBookingId
        }
        /* Guest checkout: Pass orderNumber and slotBookingID parameter */

        delete requestObj.apiVersion

        const response = await this.$axiosInstance.post(
          `${params.apiVersion == 'v1' ? 'v1' : 'v2'}/Cart`,
          requestObj
        )

        if (response.data?.data?.orderNumber) {
          if (
            this.cartNumber &&
            this.cartNumber === response.data?.data?.orderNumber
          ) {
            this.addEditCartDetail({
              cartId: response.data.data.orderNumber,
              cartDetail: response.data.data,
            })
          } else {
            this.pushCartDetail({
              cartId: response.data.data.orderNumber,
              cartDetail: response.data.data,
            })
          }

          this.cartNumber = response.data.data.orderNumber

          /* Store guest cart number for guest checkout */
          if (
            !isLoggedIn &&
            guestCartNumber != response.data.data.orderNumber
          ) {
            guestStore.resetGuestUserInfo()
            guestStore.setGuestCartNumber(response.data.data.orderNumber)
          }
          /* Store guest cart number for guest checkout */

          const currentRoute = this.$router.currentRoute.value
          if (currentRoute.name == 'Cart' && params.apiVersion !== 'v1')
            this.getCartDetail(false)

          return response.data
          // return response.data.data
        }
      } catch (error) {
        if (error.errors && error.errors.length) {
          error.errors.forEach((error) => {
            if (
              error.errorCode !== 'CRT_400' &&
              error.errorCode !== 'CRT_0114' &&
              error.errorCode !== 'CRT_1010'
            ) {
              NotifyMethods.showError(error.descreption)
            }
          })
          // if (error.errors[0].errorCode !== 'CRT_400')
        }
        this.loading = false
        return error
      }
    },
    async deleteCartItem({ orderItemId, removeOutOfStockProduct = false }) {
      try {
        const authStore = useAuthStore(),
          guestStore = useGuestStore(),
          persistedLocalStore = usePersistedLocalStore(),
          persistedStore = usePersistedStore()

        let cartNumber = this.cartNumber,
          isLoggedIn = authStore.isLoggedIn,
          guestCartNumber = guestStore.guestCartNumber,
          fulfillmentId = persistedStore.selectedFulfillment?.fulfillmentId

        let response = await this.$axiosInstance.delete(
          `v2/Cart/${orderItemId}`
        )

        if (response?.data) {
          if (!isLoggedIn) cartNumber = guestCartNumber

          let itemLength = Object.keys(
            this.currentCartDetails?.fulfillments?.[fulfillmentId]
              ?.shoppingCartItems || {}
          ).length

          if (itemLength > 1) {
            this.setDeleteCartItem({
              orderItemId,
              cartNumber,
              fulfillmentId,
            })
            // Remove cart info from local storage
            let cartPersistedInfo =
              persistedLocalStore.getCartPersistedInfo(cartNumber)
            if (
              cartPersistedInfo?.subscriptionDetails &&
              cartPersistedInfo?.subscriptionDetails[orderItemId]
            ) {
              persistedLocalStore.removeItemSubscription({
                cartNumber,
                orderItemId,
              })
            }
          } else {
            this.pushCartDetail({
              cartId: cartNumber,
              cartDetail: {},
            })

            this.cartNumber = null
            this.payAtStore = false
            persistedLocalStore.setField({
              field: 'isContactlessdelivery',
              value: false,
            })

            if (!isLoggedIn && guestCartNumber) guestStore.resetGuestUserInfo()

            // Remove cart info from local storage
            persistedLocalStore.setCartPersistedInfo({
              cartNumber: cartNumber,
            })
            if (this.isConfirmSubscription) this.isConfirmSubscription = false
          }

          if (removeOutOfStockProduct)
            this.removeOutOfStockProduct({
              cartId: cartNumber,
              orderItemId,
            })
          this.removeRecentSubstitute(orderItemId)
        }

        const currentRoute = this.$router.currentRoute.value
        if (currentRoute.name == 'Cart') this.getCartDetail(false)
      } catch (error) {
        if (
          error?.errors &&
          Array.isArray(error.errors) &&
          error.errors.length &&
          error.errors[0].errorCode == 'CRT_0108'
        ) {
          this.getCartDetail(false)
          // reloadPage()

          // let cartNumber = getters['currentCartNumber'],
          //   isLoggedIn = rootGetters['auth/isLoggedIn'],
          //   guestCartNumber = rootGetters['guest/guestCartNumber']
          // if (cartNumber) {
          //   let requestObj = {}
          //   if (!isLoggedIn && guestCartNumber)
          //     requestObj.orderNumber = guestCartNumber
          //   let { data } = await CartAgent.getCustomerCart(requestObj)
          //   commit('SET_CURRENT_CART_NUMBER', data.data.orderNumber)
          //   commit('PUSH_CART_DETAIL', {
          //     cartId: data.data.orderNumber,
          //     cartDetail: data.data ? data.data : {},
          //   })
          // }
        }
      }
    },
    async emptyCart() {
      const authStore = useAuthStore(),
        guestStore = useGuestStore(),
        persistedLocalStore = usePersistedLocalStore(),
        persistedStore = usePersistedStore()

      let cartNumber = this.cartNumber,
        isLoggedIn = authStore.isLoggedIn
      if (!cartNumber) return

      try {
        await this.$axiosInstance.delete(`v1/Cart/Empty/${cartNumber}`, {
          params: {
            locationID: persistedStore.currentLocationId,
          },
        })

        this.pushCartDetail({
          cartId: cartNumber,
          cartDetail: {},
          refresh: true,
        })

        this.cartNumber = null
        this.payAtStore = false
        /* remove guest cart number for guest checkout */
        if (!isLoggedIn) {
          guestStore.resetGuestUserInfo()
          if (persistedLocalStore.persistedCartNumber)
            persistedLocalStore.setField({
              field: 'persistedCartNumber',
              value: null,
            })
        }
        /* remove guest cart number for guest checkout */
        persistedLocalStore.setField({
          field: 'isContactlessdelivery',
          value: false,
        })
        // Remove cart info from local storage
        persistedLocalStore.setCartPersistedInfo({
          cartNumber: cartNumber,
        })
        if (this.isConfirmSubscription) this.isConfirmSubscription = false
      } catch (error) {}
    },
    async updateSubstitution(params, options = {}) {
      try {
        const authStore = useAuthStore()
        let cartNumber = this.cartNumber,
          isLoggedIn = authStore.isLoggedIn,
          queryParams = {}
        if (!isLoggedIn) queryParams.orderNumber = cartNumber
        let { data } = await this.$axiosInstance.put(
          'v1/Cart/AllowSubstitute',
          params,
          { queryParams }
        )

        if (data?.data?.length) {
          let substitutions = convertArrayToObject(data.data, 'orderLineItemID')

          this.substituteDetail = Object.assign(
            {},
            this.substituteDetail,
            substitutions
          )

          if (options.resetSubstitution)
            this.recentSubstituteDetail = Object.assign({}, {})

          return true
        }

        return false
      } catch (error) {}
    },
    async updatePickupInfo(params) {
      try {
        let { data } = await this.$axiosInstance.put(
          'v1/Cart/PickupInfo',
          params
        )

        if (data.data) {
          this.setPickupInfo({
            fulfillmentMethodID: params.fulfillmentMethodID,
            pickupPerson: {
              primaryUserFirstName: params.primaryUserFirstName,
              primaryUserLastName: params.primaryUserLastName,
              pickUpByPhone1: params.pickUpByPhone1,
              pickUpByEmail1: params.pickUpByEmail1,
              pickupByDOB1: params.pickupByDOB1,
              alternateUserFirstName: params.alternateUserFirstName,
              alternateUserLastName: params.alternateUserLastName,
              pickUpByPhone2: params.pickUpByPhone2,
              pickUpByEmail2: params.pickUpByEmail2,
              pickupByDOB2: params.pickupByDOB2,
            },
          })
        }

        return data.data
      } catch (error) {}
    },
    async updateCartAddress(params) {
      try {
        if (this.selectedShippingRateId)
          params['shippingRateID'] = this.selectedShippingRateId

        let { data } = await this.$axiosInstance.put(
          'v1/Cart/OrderAddress',
          params
        )

        if (data.success) {
          if (data.data && data.data.orderNumber) {
            this.cartNumber = data.data.orderNumber

            this.pushCartDetail({
              cartId: data.data.orderNumber,
              cartDetail: data.data,
              refresh: true,
            })
          }

          this.setOrderAddress(params)
          return data
        }
        return data
      } catch (error) {
        return error
      }
    },
    async updateCartPaymentMethod(params) {
      try {
        let { data } = await this.$axiosInstance.put(
          'v1/Cart/OrderPayments',
          params
        )

        if (data.success) {
          this.currentOrderParameters = {
            ...this.currentOrderParameters,
            paymentMethodID: params.paymentMethodId,
          }
          return true
        }
        return false
      } catch (error) {
        return false
      }
    },
    async placeOrder(params) {
      try {
        const orderStore = useOrderStore()
        let { data } = await this.$axiosInstance.post(
          'v1/Cart/PlaceOrder',
          params
        )

        if (data.success && data.data.orderNumber) {
          await orderStore.setOrderDetail({
            [data.data.orderNumber]: data.data,
          })
          await orderStore.setField({
            field: 'recentOrderNumber',
            value: data.data.orderNumber,
          })
        }

        return data.data
      } catch (error) {
        return error
      }
    },
    async getOrderThankYou(orderNumber) {
      try {
        const orderStore = useOrderStore()
        let { data } = await this.$axiosInstance.get('v1/ThankYou', {
          params: {
            OrderNumber: orderNumber,
          },
        })

        if (data.success && data.data.orderNumber) {
          await orderStore.setOrderDetail({
            [data.data.orderNumber]: data.data,
          })
          await orderStore.setField({
            field: 'recentOrderNumber',
            value: data.data.orderNumber,
          })
        }
      } catch (error) {}
    },
    async resetCart(params) {
      const authStore = useAuthStore(),
        guestStore = useGuestStore(),
        persistedLocalStore = usePersistedLocalStore()

      const isLoggedIn = authStore.isLoggedIn,
        cartNumber = isLoggedIn ? this.cartNumber : guestStore.guestCartNumber

      this.pushCartDetail({
        cartId: cartNumber,
        cartDetail: {},
        refresh: true,
      })

      this.cartNumber = null
      this.payAtStore = false
      /* remove guest cart number for guest checkout */
      if (!isLoggedIn) guestStore.resetGuestUserInfo()
      /* remove guest cart number for guest checkout */
      persistedLocalStore.setField({
        field: 'isContactlessdelivery',
        value: false,
      })

      // Remove cart info from local storage
      persistedLocalStore.setCartPersistedInfo({
        cartNumber: cartNumber,
      })
      if (this.isConfirmSubscription) this.isConfirmSubscription = false
    },
    toggleSubstitution(substituteProductData = {}) {
      let isSubstitutionAllowed = this.isSubstitutionAllowed,
        isAllowSubstitute = true,
        objSubstitute = {}

      if (isSubstitutionAllowed) {
        isAllowSubstitute = false
      }

      if (this.currentCartDetails?.fulfillments) {
        for (let fId in this.currentCartDetails.fulfillments) {
          const fulfillment = this.currentCartDetails.fulfillments[fId]
          if (fulfillment.codeName !== 'sd') {
            for (let olId in fulfillment.shoppingCartItems) {
              const item = fulfillment.shoppingCartItems[olId]
              if ([2, 3].includes(item.orderLineItemOrderType)) {
                continue
              }
              let params = {
                substituteProducts:
                  isAllowSubstitute &&
                  substituteProductData[item.orderLineItemID]
                    ? substituteProductData[item.orderLineItemID][
                        'substituteProducts'
                      ]
                    : [],
                selectedSubstituteProduct: isAllowSubstitute
                  ? this.selectedChildSubstituteProduct(item.orderLineItemID)
                  : null,
              }
              objSubstitute[item.orderLineItemID] = {
                orderLineItemID: item.orderLineItemID,
                allowSubstitute: isAllowSubstitute,
                ...params,
              }
            }
          }
        }
      }
      this.recentSubstituteDetail = Object.assign(
        {},
        this.recentSubstituteDetail,
        objSubstitute
      )
    },
    toggleSubstitute({
      orderLineItemId,
      allowSubstitute,
      substituteProducts = [],
    }) {
      if (orderLineItemId) {
        const objSubstitute = {
          [orderLineItemId]: {
            orderLineItemID: orderLineItemId,
            allowSubstitute: !this.substitute(orderLineItemId, allowSubstitute),
            substituteProducts: substituteProducts,
            selectedSubstituteProduct: null,
          },
        }
        this.recentSubstituteDetail = Object.assign(
          {},
          this.recentSubstituteDetail,
          objSubstitute
        )
      }
    },
    async showAlert(errors = []) {
      const authStore = useAuthStore(),
        guestStore = useGuestStore(),
        reserveSlotStore = useReserveSlotStore(),
        persistedLocalStore = usePersistedLocalStore(),
        giftCardStore = useGiftCardStore()

      errors.every((element) => {
        if (element.errorCode === 'CRT_0002') {
          let cartNumber = this.cartNumber,
            isLoggedIn = authStore.isLoggedIn,
            guestCartNumber = guestStore.guestCartNumber

          this.pushCartDetail({
            cartId: cartNumber,
            cartDetail: {},
            refresh: true,
          })
          this.cartNumber = null
          /* remove guest cart number for guest checkout */
          if (!isLoggedIn && guestCartNumber) {
            guestStore.resetGuestUserInfo()
          }
          /* remove guest cart number for guest checkout */
          persistedLocalStore.setField({
            field: 'isContactlessdelivery',
            value: false,
          })
          // Reset gift card state
          giftCardStore.setState({
            useGiftCardForCheckout: false,
            selectedExternalGiftCardForCheckout: {},
            selGiftCardForCheckout: {},
            myActiveGiftCards: {},
          })

          // let message = `You have completed Order ${cartNumber} on another device or session. Refreshing the cart to begin a new order`
          let message =
            'Your previous cart has been abandoned. Please refresh your page and you can begin creating a new order!'
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(message)
          } else {
            this.setAlert({
              alertKey: 'cartAlert',
              alertData: {
                variant: 'danger',
                message,
              },
            })
          }
          // return false
        } else if (
          element.errorCode === 'CRT_0018' ||
          element.errorCode === 'CRT_0014'
        ) {
          this.$router.push({ name: 'Cart' }).catch(() => {})
          reserveSlotStore.resetSlots()

          if (Platform?.is?.mobile) {
            NotifyMethods.showError(element.descreption)
          } else {
            this.showAlertError({ ...element, showCountDown: 10 })
          }
        } else if (element.errorCode === 'DELIVERY_QUOTE_ERROR') {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(element.descreption)
          } else {
            this.setAlert({
              alertKey: 'cartAlert',
              alertData: {
                variant: 'danger',
                message: element.descreption,
              },
            })
          }
          this.setAlert({
            alertKey: 'deliveryRateAlert',
            alertData: {
              variant: 'danger',
              message: element.descreption,
            },
          })
        } else {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(element.descreption)
          } else {
            this.setAlert({
              alertKey: 'cartAlert',
              alertData: {
                variant: 'danger',
                message: element.descreption,
              },
            })
          }
        }
        return true
      })
    },
    async applyLnsPoints(body = { dollars: 0 }) {
      try {
        let { data } = await this.$axiosInstance.post(
          'v1/Cart/ApplyLnsPoints',
          body
        )

        if (data.success && data.data)
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data,
            refresh: true,
          })

        return data
      } catch (error) {
        return error
      }
    },
    async applyCoupons(body) {
      try {
        let { data } = await this.$axiosInstance.post('v1/Cart/Coupons', body)

        if (data.success && data.data)
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data,
            refresh: true,
          })

        return data
      } catch (error) {
        return error
      }
    },
    async applyTip(body) {
      try {
        hideShowFullSpinner(true)
        if (this.selectedShippingRateId)
          body['shippingRateID'] = this.selectedShippingRateId

        let { data } = await this.$axiosInstance.post('v1/Cart/tip', body)

        if (data?.data?.orderNumber) {
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data ? data.data : {},
            refresh: true,
          })
        }

        return data
      } catch (error) {
        return error
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async removeCoupons(couponID) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `v1/Cart/Coupons/${couponID}`
        )

        if (data.success && data.data)
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data,
            refresh: true,
          })

        return data
      } catch (error) {
        return error
      }
    },
    async getOutOfStockProducts(params) {
      try {
        let response = await this.$axiosInstance.get(
          `v1/Cart/OutOfStock/Products`,
          {
            params,
          }
        )

        if (response?.data?.success) {
          let products = response.data.data.map((product) => {
            return { ...product, selectedQty: product.inStockQty }
          })
          this.outOfStockProductsInfo = Object.assign(
            {},
            this.outOfStockProductsInfo,
            {
              [params.orderNumber]: products,
            }
          )
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async updateAllOutOfStockProducts(objRequest) {
      try {
        const authStore = useAuthStore(),
          guestStore = useGuestStore()

        let cartNumber = this.cartNumber,
          isLoggedIn = authStore.isLoggedIn,
          guestCartNumber = guestStore.guestCartNumber
        let response = await this.$axiosInstance.put(
          'v1/Cart/OutOfStock/Update',
          objRequest
        )

        if (
          cartNumber &&
          response?.data?.success &&
          response.data?.data === true
        ) {
          this.outOfStockProductsInfo = Object.assign(
            {},
            this.outOfStockProductsInfo,
            {
              [cartNumber]: [],
            }
          )
          let requestObj = {}
          if (!isLoggedIn && guestCartNumber)
            requestObj.orderNumber = guestCartNumber
          let { data } = await this.$axiosInstance.get(
            'v1/Cart/GetOrderNumber',
            {
              params: requestObj,
            }
          )

          if (data?.data?.orderNumber) {
            this.cartNumber = data.data.orderNumber
            this.pushCartDetail({
              cartId: data.data.orderNumber,
              cartDetail: data.data ? data.data : {},
              refresh: true,
            })
          }
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async removeAllOutOfStockProducts({ orderLineItemIDs }) {
      try {
        const authStore = useAuthStore(),
          guestStore = useGuestStore()

        let cartHasAllOutOfStockProducts = this.cartHasAllOutOfStockProducts
        let response = await this.$axiosInstance.delete('v1/Cart/Remove', {
          data: orderLineItemIDs,
        })
        let cartNumber = this.cartNumber,
          isLoggedIn = authStore.isLoggedIn,
          guestCartNumber = guestStore.guestCartNumber
        if (
          cartNumber &&
          response &&
          response.data &&
          response.data.success &&
          response.data.data === true
        ) {
          this.outOfStockProductsInfo = Object.assign(
            {},
            this.outOfStockProductsInfo,
            {
              [cartNumber]: [],
            }
          )
          let outOfStockProducts = this.outOfStockProducts
          if (cartHasAllOutOfStockProducts && !outOfStockProducts.length) {
            await this.emptyCart()
            return response.data
          }
          if (!cartHasAllOutOfStockProducts) {
            let requestObj = {}
            if (!isLoggedIn && guestCartNumber)
              requestObj.guestCartNumber = guestCartNumber
            let { data } = await this.$axiosInstance.get(
              '/v1/Cart/GetOrderNumber',
              {
                params: requestObj,
              }
            )

            if (data?.data?.orderNumber) {
              this.cartNumber = data.data.orderNumber
              this.pushCartDetail({
                cartId: data.data.orderNumber,
                cartDetail: data.data ? data.data : {},
                refresh: true,
              })
            } else if (data.data == null) {
              const cartNumber =
                !isLoggedIn && guestCartNumber
                  ? guestCartNumber
                  : this.cartNumber
              this.pushCartDetail({
                cartId: cartNumber,
                cartDetail: {},
                refresh: true,
              })
            }
          }
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async getAllSubstituteItemsForProduct(params) {
      try {
        const persistedStore = usePersistedStore()
        let locationID = persistedStore.currentLocationId
        let response = await this.$axiosInstance.post(
          `v1/Cart/AllSubstituteItemsForProduct?locationID=${locationID}`,
          params
        )

        return response.data
      } catch (error) {
        return error
      }
    },
    toggleSubstituteChildProduct({
      orderLineItemID,
      substituteProductID,
      allowSubstitute,
      originalLineItemID = null,
    }) {
      if (orderLineItemID) {
        const objSubstitute = {
          [orderLineItemID]: {
            orderLineItemID: orderLineItemID,
            allowSubstitute: this.substitute(orderLineItemID, allowSubstitute),
            substituteProducts: this.substituteChildProductDetails(
              orderLineItemID,
              allowSubstitute
            ),
            selectedSubstituteProduct: substituteProductID,
            previousOrderLineItemID: originalLineItemID,
          },
        }
        this.recentSubstituteDetail = Object.assign(
          {},
          this.recentSubstituteDetail,
          objSubstitute
        )
      }
    },
    async getShippingRates({ showFullLoader = false, additionalParams = {} }) {
      try {
        const authStore = useAuthStore()
        // const persistedLocalStore = usePersistedLocalStore()

        if (showFullLoader) {
          hideShowFullSpinner(true)
        }

        this.loading = true

        /* Start: Guest Checkout Changes */
        let params = {},
          isLoggedIn = authStore.isLoggedIn,
          cartNumber = this.cartNumber
        /* End: Guest Checkout Changes */
        if (!isLoggedIn && cartNumber) {
          params.orderNumber = cartNumber
        }

        let { data } = await this.$axiosInstance.get('v1/Cart/Shipping/Rates', {
          params: { ...params, ...additionalParams },
        })

        if (data?.data) {
          const rateObjData = convertArrayToObject(data.data, 'rateID')
          this.shippingRates = rateObjData

          // if (this.cartNumber) {
          //   const cartPersistedInfo = persistedLocalStore.getCartPersistedInfo(
          //     this.cartNumber
          //   )
          //   const currentRateId = cartPersistedInfo?.rateID || ''

          //   /* if (currentRateId && !rateObjData[currentRateId]) {
          //     persistedLocalStore.setCartPersistedInfo({
          //       cartNumber: this.cartNumber,
          //     })
          //     currentRateId = ''
          //   } */

          //   if (
          //     !currentRateId ||
          //     (currentRateId && !rateObjData[currentRateId])
          //   ) {
          //     if (data.data[0]) {
          //       persistedLocalStore.setCartPersistedInfo({
          //         cartNumber: this.cartNumber,
          //         data: {
          //           ...(cartPersistedInfo || {}),
          //           rateID: data.data[0].rateID,
          //           deliveryInstruction: '',
          //         },
          //       })
          //     }
          //     // else {
          //     //   persistedLocalStore.setCartPersistedInfo({
          //     //     cartNumber: this.cartNumber,
          //     //   })
          //     // }
          //   }

          //   // !persistedLocalStore.getCartPersistedInfo(this.cartNumber)
          // }
        }
        return data?.data || []
      } catch (error) {
      } finally {
        hideShowFullSpinner(false)
        this.loading = false
      }
    },
    async getDeliveryRates(params = {}, additionalParams = {}) {
      try {
        const authStore = useAuthStore()
        this.loading = true
        if (!Object.values(params).length) this.rateLoader = true

        /* Start: Guest Checkout Changes */
        let requestObj = {
            ...params,
          },
          isLoggedIn = authStore.isLoggedIn,
          cartNumber = this.cartNumber
        /* End: Guest Checkout Changes */
        if (!isLoggedIn && cartNumber) {
          requestObj.orderNumber = cartNumber
        }

        let { data } = await this.$axiosInstance.get('v1/Cart/Delivery/Rate', {
          params: { ...requestObj, ...additionalParams },
        })

        if (data?.data?.orderNumber) {
          this.cartNumber = data.data.orderNumber
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data ? data.data : {},
            refresh: true,
          })

          if (data?.data?.deliveryPartnerDetails) {
            let deliveryPartnerDetails = convertArrayToObject(
              data.data.deliveryPartnerDetails,
              'shippingPartnerID'
            )
            this.deliveryPartners = deliveryPartnerDetails
          }
        }
      } catch (error) {
        this.deliveryPartners = {}
        return error
      } finally {
        this.loading = false
        this.rateLoader = false
      }
    },
    async requestForGuestOtp(body) {
      try {
        let response = await this.$axiosInstance.post('v1/Cart/Guest/Otp', body)
        return response.data
      } catch (error) {
        return error
      }
    },
    async verifyGuestOtp(body) {
      try {
        const guestStore = useGuestStore()
        let response = await this.$axiosInstance.post(
          'v1/Cart/Guest/Otp/Verify',
          body
        )

        if (response.data?.success && response.data?.data?.isVerified) {
          guestStore.setGuestUser({
            guestUser: {
              firstName: body.firstName,
              lastName: body.lastName,
              email: body.email,
              customerID: response.data.data.customerID,
            },
            setCustomerID: true,
          })
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async fetchGuestUserInfo(customerID, saveState = true) {
      try {
        const guestStore = useGuestStore()
        let response = await this.$axiosInstance.get(
          `v1/Cart/Guest/Customer/${customerID}`
        )

        if (response.data?.success && response.data?.data && saveState) {
          guestStore.setGuestUser({ guestUser: response.data.data })
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async verifyGuestAge() {
      try {
        const guestStore = useGuestStore()
        let guestCartNumber = guestStore.guestCartNumber
        let response = await this.$axiosInstance.post(
          `v1/Cart/Guest/Age/Verify?orderNumber=${guestCartNumber}`,
          {}
        )

        return response.data
      } catch (error) {
        return error
      }
    },
    async applyDiscountCoupons(body) {
      try {
        let { data } = await this.$axiosInstance.post(
          'v1/Cart/DiscountCoupons',
          body
        )

        if (data.success && data.data)
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data,
            refresh: true,
          })

        return data
      } catch (error) {
        return error
      }
    },
    async removeDiscountCoupons(body) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `v1/Cart/DiscountCoupons/${body.couponID}/${body.orderNumber}`
        )

        if (data.success && data.data)
          this.pushCartDetail({
            cartId: data.data.orderNumber,
            cartDetail: data.data,
            refresh: true,
          })

        return data
      } catch (error) {
        return error
      }
    },
    async fetchShippingStores(params = {}) {
      try {
        let response = await this.$axiosInstance.get(
          `v1/Cart/ShipToStoreLocation`,
          params
        )

        return response.data
      } catch (error) {
        return error
      }
    },
    async applyShipToStoreLocation(params = {}) {
      try {
        let response = await this.$axiosInstance.put(
          `v1/Cart/ApplyShipToStoreLocation`,
          {},
          { params }
        )

        return response.data
      } catch (error) {
        return error
      }
    },
    setField({ field, value }) {
      this[field] = value
    },
    showAlertError(errorObject) {
      this.alertDetail = Object.assign({}, errorObject)
    },
    setAlert({ alertKey, alertData }) {
      if (typeof alertData === 'object' && alertData !== null)
        this.alertInfo = { ...this.alertInfo, [alertKey]: alertData }
      else this.alertInfo = { ...this.alertInfo, [alertKey]: alertData }
    },
    setOrderAddress(addressInfo) {
      let detail = this.currentOrderParameters
      for (let key in detail.orderFulfillments) {
        let fulfillment = detail.orderFulfillments[key]
        if (
          addressInfo.fulfillmentMethodID == fulfillment.fulfillmentMethodID
        ) {
          detail.orderFulfillments[key].addressID = addressInfo.addressID
        }
      }
      this.currentOrderParameters = Object.assign({}, detail)
    },
    pushCartDetail({ cartId, cartDetail }) {
      if (!Object.keys(cartDetail).length) {
        delete this.cartDetail[cartId]
      } else {
        let localCartDetail = {
          ...cartDetail,
          fulfillments: {},
        }

        if (cartDetail?.fulfillments) {
          cartDetail.fulfillments.forEach((fulfillment) => {
            let breakIndex

            for (
              let i = fulfillment.shoppingCartItems.length - 1;
              i >= 0;
              i--
            ) {
              if (!fulfillment.shoppingCartItems[i].isLinkedProduct) break
              else {
                fulfillment.shoppingCartItems.every((shoppingCartItem) => {
                  if (
                    shoppingCartItem.orderLineItemID ==
                    fulfillment.shoppingCartItems[i].parentLinkedLineItemId
                  ) {
                    if (!shoppingCartItem.linkedProducts)
                      shoppingCartItem.linkedProducts = []

                    shoppingCartItem.linkedProducts.push(
                      fulfillment.shoppingCartItems[i]
                    )

                    return false
                  }
                  return true
                })

                breakIndex = i
              }
            }

            let shoppingCartItems = {}

            // if (
            //   this.cartDetail[cartId]?.fulfillments?.[
            //     fulfillment.fulfillmentMethodID
            //   ]?.shoppingCartItems &&
            //   !refresh
            // ) {
            //   shoppingCartItems = {
            //     ...this.cartDetail[cartId]?.fulfillments?.[
            //       fulfillment.fulfillmentMethodID
            //     ]?.shoppingCartItems,
            //   }
            // }

            for (let s = 0; s < fulfillment.shoppingCartItems.length; s++) {
              const item = fulfillment.shoppingCartItems[s]

              if (item?.substituteProductDetails?.length > 0) {
                item.substituteProductDetails.push({
                  image: '',
                  price: 0,
                  productID: '00000000-0000-0000-0000-000000000000',
                  productName: 'No Preference',
                  qtyOnHand: 0,
                  seoName: '',
                  substituteProductID: '00000000-0000-0000-0000-000000000000',
                })
              }

              shoppingCartItems[item.orderLineItemID] = item

              if (breakIndex == s + 1) break
            }

            localCartDetail.fulfillments = {
              ...localCartDetail.fulfillments,
              [fulfillment.fulfillmentMethodID]: {
                ...fulfillment,
                shoppingCartItems,
              },
            }
          })
        }

        localCartDetail = calculateCartTotal(localCartDetail)
        this.cartDetail = Object.assign({}, this.cartDetail, {
          [cartId]: localCartDetail,
        })
      }
    },
    addEditCartDetail({ cartId, cartDetail }) {
      if (this.cartDetail[cartId]) {
        let localCartDetail = {
          ...this.cartDetail[cartId],
        }

        if (cartDetail?.fulfillments) {
          cartDetail.fulfillments.forEach((fulfillment) => {
            let breakIndex

            for (
              let i = fulfillment.shoppingCartItems.length - 1;
              i >= 0;
              i--
            ) {
              if (!fulfillment.shoppingCartItems[i].isLinkedProduct) break
              else {
                fulfillment.shoppingCartItems.every((shoppingCartItem) => {
                  if (
                    shoppingCartItem.orderLineItemID ==
                    fulfillment.shoppingCartItems[i].parentLinkedLineItemId
                  ) {
                    if (!shoppingCartItem.linkedProducts)
                      shoppingCartItem.linkedProducts = []

                    shoppingCartItem.linkedProducts.push(
                      fulfillment.shoppingCartItems[i]
                    )

                    return false
                  }
                  return true
                })

                breakIndex = i
              }
            }

            for (let s = 0; s < fulfillment.shoppingCartItems.length; s++) {
              const item = fulfillment.shoppingCartItems[s]

              if (item?.substituteProductDetails?.length > 0) {
                item.substituteProductDetails.push({
                  image: '',
                  price: 0,
                  productID: '00000000-0000-0000-0000-000000000000',
                  productName: 'No Preference',
                  qtyOnHand: 0,
                  seoName: '',
                  substituteProductID: '00000000-0000-0000-0000-000000000000',
                })
              }

              if (
                localCartDetail.fulfillments?.[fulfillment.fulfillmentMethodID]
                  ?.shoppingCartItems
              ) {
                localCartDetail.fulfillments[
                  fulfillment.fulfillmentMethodID
                ].shoppingCartItems[item.orderLineItemID] = {
                  ...(localCartDetail.fulfillments[
                    fulfillment.fulfillmentMethodID
                  ].shoppingCartItems[item.orderLineItemID] || {}),
                  ...item,
                }
              }

              if (breakIndex == s + 1) break
            }
          })
        }

        localCartDetail = calculateCartTotal(localCartDetail)

        this.cartDetail = Object.assign({}, this.cartDetail, {
          [cartId]: localCartDetail,
        })
      }
    },
    setDeleteCartItem({ orderItemId, cartNumber, fulfillmentId }) {
      if (this.cartDetail[cartNumber]) {
        let localCartDetail = {
          ...this.cartDetail[cartNumber],
        }

        delete localCartDetail.fulfillments[fulfillmentId].shoppingCartItems[
          orderItemId
        ]

        localCartDetail = calculateCartTotal(localCartDetail)

        this.cartDetail = Object.assign({}, this.cartDetail, {
          [cartNumber]: localCartDetail,
        })
      }
    },
    removeRecentSubstitute(orderItemId) {
      delete this.recentSubstituteDetail[orderItemId]
    },
    removeOutOfStockProduct({ cartId, orderItemId }) {
      let products = this.outOfStockProductsInfo[cartId].filter(
        (outOfStockProduct) => outOfStockProduct.orderLineItemID != orderItemId
      )
      this.outOfStockProductsInfo = Object.assign(
        {},
        this.outOfStockProductsInfo,
        {
          [cartId]: products,
        }
      )
    },
    setPickupInfo(pickupInfo) {
      let detail = this.currentOrderParameters
      for (let key in detail.orderFulfillments) {
        let fulfillment = detail.orderFulfillments[key]
        if (pickupInfo.fulfillmentMethodID == fulfillment.fulfillmentMethodID) {
          detail.orderFulfillments[key].pickupPerson = pickupInfo.pickupPerson
        }
      }
      this.currentOrderParameters = Object.assign({}, detail)
    },
    countDownChange(dismissCountDown) {
      this.alertDetail = Object.assign({}, this.alertDetail, {
        showCountDown: dismissCountDown,
      })
    },
    setCheckoutSelection(data = null) {
      if (data) {
        this.checkoutSelection = {
          ...(this.checkoutSelection || {}),
          ...data,
        }
      } else if (this.checkoutSelection) this.checkoutSelection = null
    },
    setRecentSubstitute(substitute) {
      this.recentSubstituteDetail = Object.assign(
        {},
        this.recentSubstituteDetail,
        substitute
      )
    },
    resetRecentSubstitute() {
      this.recentSubstituteDetail = Object.assign({}, {})
    },
    setAcknowledgementAccepted(val) {
      this.currentOrderParameters = {
        ...this.currentOrderParameters,
        isAcknowledgeAccepted: val,
      }
    },
    setCartPaymentMethod(paymentMethodId) {
      this.currentOrderParameters = {
        ...this.currentOrderParameters,
        paymentMethodID: paymentMethodId,
      }
    },
    setCurrentOrderDetail(orderDetail) {
      this.currentOrderParameters = Object.assign({}, orderDetail)
    },
    updateRecentSubstituteOnEditOrder(order) {
      if (
        Object.keys(this.recentSubstituteDetail) &&
        Object.keys(this.recentSubstituteDetail).length > 0 &&
        order &&
        order[Object.keys(order)[0]]?.trackOrderLineItems
      ) {
        order[Object.keys(order)[0]].trackOrderLineItems.forEach((lineItem) => {
          if (
            (this.recentSubstituteDetail[lineItem.originalLineItemID] ||
              this.recentSubstituteDetail[lineItem.orderLineItemID]) &&
            lineItem.originalLineItemID !=
              '00000000-0000-0000-0000-000000000000'
          ) {
            let substitute = this.recentSubstituteDetail[
              lineItem.originalLineItemID
            ]
              ? this.recentSubstituteDetail[lineItem.originalLineItemID]
              : this.recentSubstituteDetail[lineItem.orderLineItemID]

            if (substitute) {
              const orderLineItemID = this.recentSubstituteDetail[
                lineItem.originalLineItemID
              ]
                ? lineItem.originalLineItemID
                : lineItem.orderLineItemID
              this.removeRecentSubstitute(orderLineItemID)
              this.setRecentSubstitute({
                [lineItem.orderLineItemID]: {
                  previousOrderLineItemID: lineItem.originalLineItemID,
                  allowSubstitute: substitute.allowSubstitute,
                  orderLineItemID: lineItem.orderLineItemID,
                  selectedSubstituteProduct:
                    substitute.selectedSubstituteProduct,
                  substituteProducts: substitute.substituteProducts,
                },
              })
            }
          } else if (
            Object.values(this.recentSubstituteDetail).find(
              (x) =>
                x.previousOrderLineItemID == lineItem.originalLineItemID ||
                x.previousOrderLineItemID == lineItem.orderLineItemID
            ) &&
            lineItem.originalLineItemID
          ) {
            let substituteElse = Object.values(
              this.recentSubstituteDetail
            ).find(
              (x) =>
                x.previousOrderLineItemID == lineItem.originalLineItemID ||
                x.previousOrderLineItemID == lineItem.orderLineItemID
            )
            if (
              substituteElse.previousOrderLineItemID !=
              '00000000-0000-0000-0000-000000000000'
            ) {
              this.removeRecentSubstitute(substituteElse.orderLineItemID)
              this.setRecentSubstitute({
                [lineItem.orderLineItemID]: {
                  previousOrderLineItemID: lineItem.originalLineItemID,
                  allowSubstitute: substituteElse.allowSubstitute,
                  orderLineItemID: lineItem.orderLineItemID,
                  selectedSubstituteProduct:
                    substituteElse.selectedSubstituteProduct,
                  substituteProducts: substituteElse.substituteProducts,
                },
              })
            }
          }
        })
      } else if (
        !Object.keys(this.recentSubstituteDetail) ||
        (Object.keys(this.recentSubstituteDetail).length <= 0 &&
          order &&
          order[Object.keys(order)[0]]?.trackOrderLineItems)
      ) {
        order[Object.keys(order)[0]].trackOrderLineItems.forEach((lineItem) => {
          if (
            lineItem.preferredSubstituteProductID &&
            lineItem.preferredSubstituteProductID !=
              '00000000-0000-0000-0000-000000000000'
          ) {
            lineItem.substituteProductListData.forEach((substitutes) => {
              let selectedSub = substitutes.substituteProductDetails.find(
                (x) =>
                  x.substituteProductID == lineItem.preferredSubstituteProductID
              )
              if (selectedSub) {
                this.setRecentSubstitute({
                  [lineItem.orderLineItemID]: {
                    allowSubstitute: true,
                    orderLineItemID: lineItem.orderLineItemID,
                    selectedSubstituteProduct: selectedSub.substituteProductID,
                    substituteProducts: [],
                  },
                })
              }
            })
          }
        })
      }
    },
    setGiftCardAcknowlegment(val) {
      this.currentOrderParameters = {
        ...this.currentOrderParameters,
        isGiftCardAllowForRecurringOrder: val,
      }
    },
    setDeliveryAcknowledgeMent(val) {
      this.currentOrderParameters = {
        ...this.currentOrderParameters,
        isAcknowledgedForDelivery: val,
      }
    },
    async verifyUberSandboxOtp(body) {
      try {
        let response = await this.$axiosInstance.post(
          'v1/Cart/Verify/SandBoxCode',
          body
        )
        return response.data
      } catch (error) {
        return error
      }
    },
    async createCheckoutSession(params) {
      try {
        const persistedStore = usePersistedStore(),
          persistedLocalStore = usePersistedLocalStore(),
          authStore = useAuthStore(),
          guestStore = useGuestStore()

        this.loading = true
        let requestObj = {
          ...params,
          locationID: persistedStore.currentLocationId,
          zipCode: persistedStore.locationOrShippingZip,
        }

        const response = await this.$axiosInstance.post(
          'v1/Cart/BuyNow',
          requestObj
        )

        if (response.data?.data?.orderNumber) {
          if (
            this.cartNumber &&
            this.cartNumber != response.data.data.orderNumber
          ) {
            persistedLocalStore.setField({
              field: 'persistedCartNumber',
              value: this.cartNumber,
            })
          }
          this.cartNumber = response.data.data.orderNumber

          if (
            !authStore.isLoggedIn &&
            guestStore.guestCartNumber != response.data.data.orderNumber
          ) {
            guestStore.resetGuestUserInfo()
            guestStore.setGuestCartNumber(response.data.data.orderNumber)
          }

          return response.data
        }
      } catch (error) {
        this.loading = false
        return error
      }
    },
  },
})
